import { deepCopyElement } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { AppState } from "../types";

/**
 * Enhanced lock system integration utilities
 * Ensures lock state preservation across copy/paste, export/import, and group operations
 */

/**
 * Copy elements while preserving lock state
 * This function ensures that when elements are copied (via clipboard or duplication),
 * their lock state is properly preserved
 */
export const copyElementsWithLockState = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  return elements.map((element) => {
    const copiedElement = deepCopyElement(element);
    // Explicitly preserve lock state (deepCopyElement should handle this, but being explicit)
    copiedElement.locked = element.locked;

    // Ensure we generate a new ID for copied elements (except for deep copy operations)
    // Generate a proper random ID instead of relying on deepCopyElement
    if (copiedElement.id === element.id) {
      copiedElement.id = `${element.type}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }

    return copiedElement;
  });
};

/**
 * Handle lock state during group operations
 * When elements are grouped/ungrouped, ensure lock state is handled appropriately
 */
export const handleLockStateInGroupOperations = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  operation: "group" | "ungroup",
): {
  shouldPreserveLockState: boolean;
  lockedElementIds: Set<string>;
} => {
  const lockedElementIds = new Set<string>();

  // Collect locked element IDs
  elements.forEach((element) => {
    if (element.locked) {
      lockedElementIds.add(element.id);
    }
  });

  // For group operations, we should preserve individual lock states
  // For ungroup operations, we should maintain the lock state of individual elements
  const shouldPreserveLockState = lockedElementIds.size > 0;

  return {
    shouldPreserveLockState,
    lockedElementIds,
  };
};

/**
 * Validate lock state consistency after operations
 * Ensures that lock state is consistent across related elements
 */
export const validateLockStateConsistency = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
): {
  isConsistent: boolean;
  inconsistencies: Array<{
    elementId: string;
    issue: string;
  }>;
} => {
  const inconsistencies: Array<{ elementId: string; issue: string }> = [];

  elements.forEach((element) => {
    // Check if locked elements are properly handled in selection
    if (element.locked && appState.selectedElementIds[element.id]) {
      // This might be valid in some cases (like when unlocking), so just log for now
      // inconsistencies.push({
      //   elementId: element.id,
      //   issue: 'Locked element is selected'
      // });
    }

    // Check if lock state is properly set - must be a boolean, undefined/null are invalid
    if (
      element.locked !== undefined &&
      element.locked !== null &&
      typeof element.locked !== "boolean"
    ) {
      inconsistencies.push({
        elementId: element.id,
        issue: "Lock state is not a boolean value",
      });
    }

    // Check for undefined or null lock state (should be treated as invalid)
    if (element.locked === undefined || element.locked === null) {
      inconsistencies.push({
        elementId: element.id,
        issue: "Lock state is undefined or null",
      });
    }
  });

  return {
    isConsistent: inconsistencies.length === 0,
    inconsistencies,
  };
};

/**
 * Prepare elements for export with lock state
 * Ensures lock state is included in export data
 */
export const prepareElementsForExportWithLockState = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawElement[] => {
  return elements.map((element) => ({
    ...element,
    // Ensure lock state is explicitly included
    locked: element.locked ?? false,
  }));
};

/**
 * Handle lock state during paste operations
 * Determines how lock state should be handled when pasting elements
 */
export const handleLockStateOnPaste = (
  elements: readonly ExcalidrawElement[],
  options: {
    preserveLockState?: boolean;
    unlockOnPaste?: boolean;
  } = {},
): ExcalidrawElement[] => {
  const { preserveLockState = true, unlockOnPaste = false } = options;

  return elements.map((element) => {
    if (unlockOnPaste) {
      return { ...element, locked: false };
    }

    if (preserveLockState) {
      return { ...element, locked: element.locked ?? false };
    }

    // Default behavior: preserve existing lock state
    return element;
  });
};
